import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Eye, Pencil, Trash2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Store } from '../../Store';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import StatusLoadingOverlay from '../../components/StatusLoadingOverlay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import ConfirmModal from '../../components/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import { getError } from '../../utils';
import { downloadRifaTicketPreview } from '../../utils/downloadRifaTicketPreview';
import rifaService from '../../services/rifa.service';

const PREVIEW_STATUS_KEYS = [
  'reservation.ticketDownloadPreparing',
  'reservation.ticketDownloadGenerating',
  'reservation.ticketDownloadRendering',
  'reservation.ticketDownloadDownloading',
];

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS': {
      const rifas = Array.isArray(action.payload.rifas) ? action.payload.rifas : [];
      return {
        ...state,
        rifas,
        loading: false,
        itemQuantity: action.payload.totalAmount || rifas.length,
      };
    }
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload, rifas: [], itemQuantity: 0 };
    case 'DELETE_REQUEST':
      return { ...state, loading: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return { ...state, loading: false, successDelete: true };
    case 'DELETE_FAIL':
      return { ...state, loading: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loading: false, successDelete: false };
    default:
      return state;
  }
};

function rifaTipoLabel(tipo) {
  if (tipo === 'board') return 'Tablero';
  if (tipo === 'standard') return 'Estándar';
  return tipo || '—';
}

export default function Rifas() {
  const { t } = useTranslation();
  const [{ loading, error, rifas, successDelete, itemQuantity }, dispatch] = useReducer(reducer, {
    rifas: [],
    loading: true,
    error: '',
    itemQuantity: 0,
  });

  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();

  const [rifaToDelete, setRifaToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [previewOverlay, setPreviewOverlay] = useState(false);
  const itemsPerPage = 10;

  const fetchData = async skip => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const raw = await rifaService.getRifas(skip, itemsPerPage);
      let payload;
      if (raw == null) {
        payload = { rifas: [], totalAmount: 0 };
      } else if (Array.isArray(raw)) {
        payload = { rifas: raw, totalAmount: raw.length };
      } else if (Array.isArray(raw.rifas)) {
        payload = {
          rifas: raw.rifas,
          totalAmount: raw.totalAmount ?? raw.total ?? raw.rifas.length,
        };
      } else if (Array.isArray(raw.data)) {
        payload = { rifas: raw.data, totalAmount: raw.total ?? raw.totalCount ?? raw.data.length };
      } else if (raw._id) {
        payload = { rifas: [raw], totalAmount: 1 };
      } else {
        payload = { rifas: [], totalAmount: 0 };
      }
      dispatch({ type: 'FETCH_SUCCESS', payload });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData(0);
    }
  }, [successDelete, userInfo]);

  const onPageChange = async pageNumber => {
    setCurrentPage(pageNumber);
    await fetchData((pageNumber - 1) * itemsPerPage);
  };

  const handleDeleteClick = rifa => {
    setRifaToDelete(rifa);
    setShowModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      dispatch({ type: 'DELETE_REQUEST' });
      await rifaService.deleteRifa(rifaToDelete._id);
      dispatch({ type: 'DELETE_SUCCESS' });
      toast.success(t('raffle.raffleDeleted'));
      setShowModal(false);
      setRifaToDelete(null);
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'DELETE_FAIL' });
    }
  };

  const handlePreviewTicket = async rifaId => {
    setPreviewOverlay(true);
    try {
      await downloadRifaTicketPreview(rifaId);
    } catch (err) {
      toast.error(getError(err) || t('raffle.previewTicketError'));
    } finally {
      setPreviewOverlay(false);
    }
  };

  return (
    <div className='space-y-6'>
      {previewOverlay && <StatusLoadingOverlay messageKeys={PREVIEW_STATUS_KEYS} />}
      <ConfirmModal
        show={showModal}
        title={t('raffle.deleteRaffle')}
        message={t('raffle.confirmDelete')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowModal(false);
          setRifaToDelete(null);
        }}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        destructive
      />

      <PageHeader
        title={t('raffle.raffleList')}
        subtitle='Administrá todos tus bonos desde aquí'
        actions={
          <Button onClick={() => navigate('/AdminScreen/addRifa')}>
            <Plus className='h-4 w-4' />
            {t('raffle.newRaffle')}
          </Button>
        }
      />

      <Card>
        <CardContent className='p-0'>
          {loading ? (
            <div className='divide-y divide-border'>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className='flex items-center gap-4 px-6 py-4'>
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/6 animate-pulse rounded bg-muted' />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className='px-6 py-5 text-sm text-destructive'>{error}</p>
          ) : rifas.length === 0 ? (
            <div className='px-6 py-12 text-center text-sm text-muted-foreground'>
              <p>No hay bonos todavía.</p>
              <div className='mt-3'>
                <Button size='sm' onClick={() => navigate('/AdminScreen/addRifa')}>
                  Crear el primer bono
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.name')}</TableHead>
                  <TableHead>{t('common.description')}</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className='text-right'>{t('raffle.ticketQuantity')}</TableHead>
                  <TableHead className='text-right'>{t('raffle.ticketPrice')}</TableHead>
                  <TableHead className='w-[160px]' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rifas.map(rifa => (
                  <TableRow key={rifa._id}>
                    <TableCell className='font-medium'>{rifa.nombre || '—'}</TableCell>
                    <TableCell className='max-w-[200px] truncate text-muted-foreground'>
                      {rifa.descripcion || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={rifa.tipo === 'board' ? 'info' : 'secondary'}>
                        {rifaTipoLabel(rifa.tipo)}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-right'>{rifa.totalTickets ?? '—'}</TableCell>
                    <TableCell className='text-right'>
                      {rifa.precioTicket != null ? `$${rifa.precioTicket}` : '—'}
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('raffle.previewTicket')}
                          className='text-muted-foreground hover:text-foreground'
                          disabled={previewOverlay}
                          onClick={() => handlePreviewTicket(rifa._id)}
                        >
                          <FileText className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.viewDetails')}
                          className='text-muted-foreground hover:text-foreground'
                          onClick={() => navigate(`/AdminScreen/rifa/${rifa._id}`)}
                        >
                          <Eye className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.edit')}
                          className='text-muted-foreground hover:text-foreground'
                          onClick={() => navigate(`/AdminScreen/editRifa/${rifa._id}`)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.delete')}
                          className='text-destructive hover:text-destructive'
                          onClick={() => handleDeleteClick(rifa)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {itemQuantity > 0 && (
        <Pagination
          className='pagination-bar'
          currentPage={currentPage}
          totalCount={itemQuantity}
          pageSize={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
